import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../utils/axios';

const FETCH_TIMEOUT_MS = 15000;

const IntegrationsStats = () => {
  const [integrations, setIntegrations] = useState([]);
  const [integrationTypes, setIntegrationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let isTimeout = false;

    const timeoutId = setTimeout(() => {
      isTimeout = true;
      controller.abort();
    }, FETCH_TIMEOUT_MS);

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [integrationsRes, typesRes] = await Promise.all([
          apiClient.get('/integrations/', { signal: controller.signal }),
          apiClient.get('/integrations/types', { signal: controller.signal })
        ]);

        clearTimeout(timeoutId);
        setIntegrations(integrationsRes.data.integrations || []);
        setIntegrationTypes(typesRes.data.integration_types || []);
      } catch (err) {
        clearTimeout(timeoutId);
        // Only set error if it was a timeout, not a cleanup abort
        if (isTimeout) {
          setError('Request timed out. Please try again later.');
        } else if (!controller.signal.aborted) {
          setError('Failed to load statistics');
        }
      } finally {
        if (!controller.signal.aborted || isTimeout) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [fetchKey]);

  const handleRetry = useCallback(() => {
    setFetchKey(prev => prev + 1);
  }, []);

  // Loading placeholder
  if (loading) {
    return (
      <div className="dashboard-card stats-card">
        <h2>Integration Statistics</h2>
        <div className="stats-grid">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat-item stat-item-placeholder">
              <span className="stat-number-placeholder"></span>
              <span className="stat-label-placeholder"></span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-card stats-card">
        <h2>Integration Statistics</h2>
        <div className="stats-error">
          <div className="error-icon">⚠️</div>
          <div className="error-text">{error}</div>
          <button className="retry-btn" onClick={handleRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const totalIntegrations = integrations.length;
  const activeIntegrations = integrations.filter(i => i.enabled).length;
  const inactiveIntegrations = integrations.filter(i => !i.enabled).length;
  const availableTypes = integrationTypes.length;

  return (
    <div className="dashboard-card stats-card">
      <h2>Integration Statistics</h2>
      <div className="stats-grid">
        <div className="stat-item">
          <span className="stat-number">{totalIntegrations}</span>
          <span className="stat-label">Total Integrations</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{activeIntegrations}</span>
          <span className="stat-label">Active</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{inactiveIntegrations}</span>
          <span className="stat-label">Inactive</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{availableTypes}</span>
          <span className="stat-label">Available Types</span>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsStats;
